<template>
  <b-container class="p-0 search-result" fluid>
    <div v-if="!searchResultList.length" class="no-search-result">検索結果はありませんでした</div>
    <div v-if="searchResultList.length" class="complement-border" />
    <div v-for="(item, index) in searchResultList" :key="index">
      <div v-if="item.type == 'topic'" class="search-item" @click="jumpPage(item.link)">
        <div class="d-flex">
          <div class="item-title">
            <div class="d-flex my-1 mx-3">
              <div class="mr-2" style="font-weight: normal">{{ `${index + 1}件目 ` }}</div>
              <div class="title">{{ item.topicTitle }}</div>
            </div>
          </div>
        </div>
        <topic-list-item
          style="pointer-events: none"
          :id="item.item.id"
          :icon="item.item.icon"
          :category="item.item.category"
          :title="item.item.title"
          :desc="item.item.desc"
          :updated-at="item.item.updatedAt"
          :pinned="item.item.pinned"
          :star="item.item.star"
          :comments="item.item.comments"
          :watches="item.item.watches"
          :messages="item.item.messages"
          :viewer="viewer"
          :search="word"
          :acl="item.acl"
        />
      </div>

      <div v-if="item.type == 'message'" class="search-item" @click="jumpPage(item.link)">
        <div class="d-flex mr-auto">
          <div class="item-title">
            <div class="d-flex my-1 mx-3">
              <div class="mr-2" style="font-weight: normal">{{ `${index + 1}件目 ` }}</div>
              <div class="title">{{ item.topicTitle }}</div>
            </div>
          </div>
        </div>
        <message-list-item
          style="pointer-events: none"
          :id="item.item.id"
          :domainId="item.item.domainId"
          :topicId="item.item.topicId"
          :title="item.item.title"
          :message="item.item.message"
          :star="item.item.star"
          :pinned="item.item.pinned"
          :icon="item.item.icon"
          :owner="item.item.owner"
          :updatedAt="item.item.updatedAt"
          :comments="item.item.comments"
          :photos="item.item.photos"
          :likes="item.item.likes"
          :like="item.item.like"
          :users="users"
          :viewer="viewer"
          :search="word"
          :acl="item.acl"
        />
      </div>

      <div v-else-if="item.type == 'comment'" class="search-item" @click="jumpPage(item.link)">
        <div class="d-flex mr-auto">
          <div class="item-title">
            <div class="d-flex my-1 mx-3">
              <div class="mr-2" style="font-weight: normal">{{ `${index + 1}件目 ` }}</div>
              <div class="title">{{ item.topicTitle }}</div>
              <div v-if="item.topicTitle" class="mx-1">/</div>
              <div class="title">{{ item.messageTitle }}</div>
            </div>
          </div>
        </div>
        <comment-list-item
          style="pointer-events: none"
          :id="item.item.id"
          :icon="item.item.icon"
          :user="item.item.owner"
          :updatedAt="item.item.updatedAt"
          :message="item.item.message"
          :likes="item.item.likes"
          :like="item.item.like"
          :photos="item.item.photos"
          :messageId="item.item.messageId"
          :users="users"
          :viewer="viewer"
          :search="word"
        />
      </div>
    </div>
    <div v-if="searchResultList.length" class="complement-border" />
  </b-container>
</template>

<style scss>
.search-result {
  width: 100%;
  position: relative;
  overflow-x: hidden;
}

.no-search-result {
  font-size: 14px;
  color: #7d9ea5;
  text-align: center;
  padding-top: 40px;
}

.item-title {
  max-width: 100%;
  font-size: 14px;
  color: white;
  font-weight: bold;
  display: inline-block;
  background-color: #00b7c6;
  white-space: nowrap;
  position: absolute;
  cursor: pointer;
}

.title {
  max-width: 30vw;
  overflow: hidden;
  text-overflow: ellipsis;
}

.search-item {
  border-top: 4px solid #00b7c6;
  border-bottom: 4px solid #00b7c6;
  border-left: 8px solid #00b7c6;
  border-right: 8px solid #00b7c6;
  background-color: #bee9eb;
  cursor: pointer;
}

.complement-border {
  width: 100%;
  height: 4px;
  background-color: #00b7c6;
}

.highlight {
  background-color: yellow;
}

/* PC用画面 */
@media (min-width: 768px) {
  .title {
    /* サイドバー 300px */
    max-width: calc((100vw - 300px) * 0.3);
  }
}
</style>

<script lang="ts">
import { defineComponent, getCurrentInstance, onMounted, ref, watch, type PropType } from "vue";
import type Vue from "vue";
import MessageListItem from "./MessageListItem.vue";
import CommentListItem from "./CommentListItem.vue";
import TopicListItem from "./TopicListItem.vue";
import { User } from "../model";
import { Route, NavigationGuardNext } from "vue-router";
import { useAppStore } from "@/store";
import { useTextProcess } from "@/composables/useTextProcess";
import type { SearchResultItemData } from "../router/index";

export default defineComponent({
  name: "search-result",
  components: {
    MessageListItem,
    CommentListItem,
    TopicListItem,
  },
  props: {
    word: { type: String, default: () => "" },
    allData: { type: Array as PropType<SearchResultItemData[]>, default: () => [] },
    users: { type: Array as PropType<User[]>, default: () => [] }, // 組織内ユーザー情報
    viewer: { type: Object as PropType<User>, default: () => User.createNotFoundUser() }, // ログインユーザ情報
    domainId: { type: String, required: true }, //!< 所属組織ID
    topicId: { type: String, default: () => "" }, //!< 所属話題ID
  },
  // beforeRouteUpdate は vue-router の in-component ガード。
  // setup() からは登録できないため、コンポーネントオプションとして保持する。
  beforeRouteUpdate(to: Route, from: Route, next: NavigationGuardNext): void {
    if (from.params.domainId != to.params.domainId || from.params.topicId != to.params.topicId) {
      useAppStore().fetchData({
        domainId: to.params.domainId,
        topicId: to.params.topicId,
        prevDomainId: from.params.domainId,
      });
    }
    next();
  },
  setup(props) {
    // 旧 TextProcessMixin → composable（1-D 利用側書き換え）
    const { searchWord } = useTextProcess();
    // $router は Step 3 で composable 化予定
    const vm = getCurrentInstance()?.proxy as Vue | undefined;

    const searchResultList = ref<SearchResultItemData[]>([]);

    const searchFromStore = (): void => {
      if (!props.allData.length || !props.word.length) {
        searchResultList.value = [];
        return;
      }
      const list = props.allData.filter((data: SearchResultItemData) => {
        switch (data.type) {
          case "topic":
            return searchWord(data.topicTitle, props.word) || searchWord(data.text, props.word);
          case "message":
            return searchWord(data.messageTitle, props.word) || searchWord(data.text, props.word);
          case "comment":
            return searchWord(data.text, props.word);
          default:
            return false;
        }
      });

      const pinned = list.filter((data) => data.pinned); // ピンあり(固定)
      const notPinned = list.filter((data) => !data.pinned); // ピンなし

      const pinnedStar = pinned.filter((data) => data.star); // ピンあり星あり(固定+星)
      const pinnedNotStar = pinned.filter((data) => !data.star); // ピンあり星なし(固定)
      const star = notPinned.filter((data) => data.star); // ピンなし星あり(お気に入り)
      const normal = notPinned.filter((data) => !data.star); // ピンなし星なし(初期状態)
      // 更新日が 新→古 順でソート
      pinnedStar.sort((l, r) => r.updatedAt.getTime() - l.updatedAt.getTime());
      pinnedNotStar.sort((l, r) => r.updatedAt.getTime() - l.updatedAt.getTime());
      star.sort((l, r) => r.updatedAt.getTime() - l.updatedAt.getTime());
      normal.sort((l, r) => r.updatedAt.getTime() - l.updatedAt.getTime());

      searchResultList.value = pinnedStar.concat(pinnedNotStar, star, normal);
    };

    watch(
      () => props.allData,
      (): void => {
        searchFromStore();
      },
      { immediate: true }
    );

    onMounted(() => {
      searchFromStore();
    });

    const jumpPage = (link: string): void => {
      if (link) {
        vm?.$router.push(link);
      }
    };

    return {
      searchResultList,
      jumpPage,
    };
  },
});
</script>
